import type { RideLocationDto } from '@/services/platform/api/model';
import { getRideLocations } from './index';

export interface RidePickupPoint {
  name: string;
  city: string | null;
  address: string | null;
  postalCode?: string | null;
  country?: string | null;
  countryCode?: string | null;
  /**
   * Null for address-book entries that were saved before geocoding was
   * supported. Callers must resolve these (e.g. via `geocodeAddress` in
   * `components/shared/booking/engine/flows/range-rover/map`) before using the point to book a ride.
   */
  lat: number | null;
  lng: number | null;
  source: string | null;
  referenceId: string | null;
}

export interface RidePickupPointsResponse {
  addressBook: RidePickupPoint[];
  venues: RidePickupPoint[];
  stores: RidePickupPoint[];
  recentTrips: RidePickupPoint[];
}

const EMPTY_RESPONSE: RidePickupPointsResponse = {
  addressBook: [],
  venues: [],
  stores: [],
  recentTrips: [],
};

function toPickupPoints(items: RideLocationDto[] | null | undefined): RidePickupPoint[] {
  return (items ?? [])
    .filter((item): item is RideLocationDto & { name: string } => !!item.name)
    .map(item => ({
      name: item.name,
      city: item.city ?? null,
      address: item.address ?? null,
      postalCode: item.postalCode ?? null,
      country: item.country ?? null,
      countryCode: item?.countryCode ?? null,
      lat: item.lat ?? null,
      lng: item.lng ?? null,
      source: item.source ?? null,
      referenceId: item.referenceId ?? null,
    }));
}

/**
 * Fetches the pickup/dropoff locations available to the current user, grouped
 * by source: saved address book entries, JNcQUOI venues, partner stores, and
 * recent trips. Wraps `getRideLocations` (see `../index`), mapping its nullable
 * DTO fields into the app's `RidePickupPoint` shape.
 *
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so venue/store names and labels are returned in the correct language.
 * @returns `RidePickupPointsResponse` on success.
 * @returns An empty response (all arrays empty) on non-2xx response or network error.
 */
export async function getRidePickupPoints(locale?: string): Promise<RidePickupPointsResponse> {
  const response = await getRideLocations(locale);
  if (!response) return EMPTY_RESPONSE;

  return {
    addressBook: toPickupPoints(response.addressBook),
    venues: toPickupPoints(response.venues),
    stores: toPickupPoints(response.stores),
    recentTrips: toPickupPoints(response.recentTrips),
  };
}
